// src/services/issueService.js
import api from '../config/axios';

const BASE = '/issues';

// ============================================================
// REFERENCE / HELPERS
// ============================================================

export const ISSUE_STATUS = {
  OPEN:        { label: 'Open',        color: 'bg-red-100 text-red-800 border-red-300' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-300' },
  RESOLVED:    { label: 'Resolved',    color: 'bg-green-100 text-green-800 border-green-300' },
  CLOSED:      { label: 'Closed',      color: 'bg-gray-100 text-gray-800 border-gray-300' },
  REOPENED:    { label: 'Reopened',    color: 'bg-orange-100 text-orange-800 border-orange-300' },
  CANCELLED:   { label: 'Cancelled',   color: 'bg-gray-100 text-gray-500 border-gray-300' },
};

export const ISSUE_PRIORITY = {
  LOW:      { label: 'Low',      color: 'bg-green-100 text-green-800 border-green-300' },
  MEDIUM:   { label: 'Medium',   color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
  HIGH:     { label: 'High',     color: 'bg-red-100 text-red-800 border-red-300' },
  CRITICAL: { label: 'Critical', color: 'bg-purple-100 text-purple-800 border-purple-300' },
};

export const ISSUE_SCOPE = {
  MILESTONE_LEVEL: { label: 'Milestone Level' },
  SITE_LEVEL:      { label: 'Site / Segment Level' },
  PROJECT_LEVEL:   { label: 'Project Level' },
};

/** Utility: get Tailwind badge classes for status */
export const getStatusBadgeClass = (status) =>
  ISSUE_STATUS[status]?.color ?? 'bg-gray-100 text-gray-700';

/** Utility: get Tailwind badge classes for priority */
export const getPriorityBadgeClass = (priority) =>
  ISSUE_PRIORITY[priority]?.color ?? 'bg-gray-100 text-gray-700';

/** Utility: get display label for status */
export const getStatusLabel = (status) =>
  ISSUE_STATUS[status]?.label ?? status;

/** Utility: get display label for priority */
export const getPriorityLabel = (priority) =>
  ISSUE_PRIORITY[priority]?.label ?? priority;

// ============================================================
// READ
// ============================================================

/**
 * Get paginated issues with filters.
 *
 * @param {object} params
 * @param {number} [params.projectId]
 * @param {string} [params.projectType]   "TOWER" | "FO"
 * @param {string} [params.status]        IssueStatus enum name
 * @param {string} [params.priority]      IssuePriority enum name
 * @param {string} [params.category]      IssueCategory enum name
 * @param {string} [params.scope]         IssueScope enum name
 * @param {number} [params.assignedToUserId]
 * @param {string} [params.keyword]
 * @param {number} [params.page]          default 0
 * @param {number} [params.size]          default 10
 * @returns Spring Page object { content, totalElements, totalPages, ... }
 */
export const getIssues = async (params = {}) => {
  const response = await api.get(BASE, { params });
  return response.data; // Spring Page
};

/**
 * Get issue detail by DB id
 * @param {number} id
 */
export const getIssueById = async (id) => {
  const response = await api.get(`${BASE}/${id}`);
  return response.data;
};

/**
 * Get issue detail by human-readable issue code (ISS-2025-0001)
 * @param {string} issueId
 */
export const getIssueByCode = async (issueId) => {
  const response = await api.get(`${BASE}/by-code/${issueId}`);
  return response.data;
};

/**
 * Get issues for a specific FO Segment
 * @param {number} segmentId
 */
export const getIssuesBySegment = async (segmentId) => {
  const response = await api.get(`${BASE}/by-segment/${segmentId}`);
  return response.data;
};

/**
 * Get issues for a specific Tower Site
 * @param {number} siteId
 */
export const getIssuesBySite = async (siteId) => {
  const response = await api.get(`${BASE}/by-site/${siteId}`);
  return response.data;
};

/**
 * Get issues for a specific Milestone
 * @param {number} milestoneId
 */
export const getIssuesByMilestone = async (milestoneId) => {
  const response = await api.get(`${BASE}/by-milestone/${milestoneId}`);
  return response.data;
};

/**
 * Get issue statistics for dashboard cards
 * @returns IssueStatistics object
 */
export const getIssueStatistics = async () => {
  const response = await api.get(`${BASE}/statistics`);
  return response.data;
};

/**
 * Get all available categories with icon and description
 * @returns Array of { value, displayName, icon, description }
 */
export const getCategories = async () => {
  const response = await api.get(`${BASE}/categories`);
  return response.data;
};

// ============================================================
// CREATE
// ============================================================

/**
 * Create a new issue (web — PM / Project Control / Supervisor)
 *
 * @param {object} data
 * @param {string} data.title
 * @param {string} data.description
 * @param {string} data.category      IssueCategory enum name
 * @param {string} data.priority      IssuePriority enum name
 * @param {string} data.issueScope    IssueScope enum name
 * @param {number} data.projectId
 * @param {number} [data.foSegmentId]
 * @param {number} [data.towerSiteId]
 * @param {number} [data.milestoneId]
 */
export const createIssue = async (data) => {
  const response = await api.post(BASE, data);
  return response.data;
};

// ============================================================
// STATUS TRANSITIONS
// ============================================================

/**
 * Assign issue to a user → moves to IN_PROGRESS
 * Actor: PM / Project Control
 *
 * @param {number} id
 * @param {object} data
 * @param {number} data.assignedToUserId
 * @param {string} [data.dueDate]          ISO date string
 * @param {string} [data.assignmentNote]
 */
export const assignIssue = async (id, data) => {
  const response = await api.put(`${BASE}/${id}/assign`, data);
  return response.data;
};

/**
 * Mark issue as RESOLVED
 * Actor: PM / Project Control
 *
 * @param {number} id
 * @param {object} data
 * @param {string} data.resolution   Required — what was done to fix it
 */
export const resolveIssue = async (id, data) => {
  const response = await api.put(`${BASE}/${id}/resolve`, data);
  return response.data;
};

/**
 * Close a RESOLVED issue
 * Actor: PM, PC, or Supervisor (confirming fix)
 *
 * @param {number} id
 */
export const closeIssue = async (id) => {
  const response = await api.put(`${BASE}/${id}/close`);
  return response.data;
};

/**
 * Reopen a RESOLVED issue (supervisor rejects resolution)
 *
 * @param {number} id
 * @param {object} data
 * @param {string} data.reopenReason
 */
export const reopenIssue = async (id, data) => {
  const response = await api.put(`${BASE}/${id}/reopen`, data);
  return response.data;
};

/**
 * Cancel an issue (PM/PC — invalid or duplicate)
 *
 * @param {number} id
 * @param {object} data
 * @param {string} data.cancelReason
 */
export const cancelIssue = async (id, data) => {
  const response = await api.put(`${BASE}/${id}/cancel`, data);
  return response.data;
};

// ============================================================
// DEFAULT EXPORT
// ============================================================

const issueService = {
  getIssues,
  getIssueById,
  getIssueByCode,
  getIssuesBySegment,
  getIssuesBySite,
  getIssuesByMilestone,
  getIssueStatistics,
  getCategories,
  createIssue,
  assignIssue,
  resolveIssue,
  closeIssue,
  reopenIssue,
  cancelIssue,
  // helpers
  getStatusBadgeClass,
  getPriorityBadgeClass,
  getStatusLabel,
  getPriorityLabel,
};

export default issueService;